/*
 * P5 — SOVEREIGN VOICE LAYER (Oracle header cluster)
 * Wake word "Command" via Web Speech API · instant local ack audio (sub-second first audio)
 * · states Standby / Listening / Executing / Reporting · ventures + revenue in the top bar
 * · Morning Oracle Brief surfaced once per day.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, Volume2, Sunrise } from "lucide-react";
import { voiceApi } from "@/lib/api-client";
import { toast } from "sonner";

const b64ToUrl = (b64) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: "audio/mpeg" }));
};

const STATE_STYLE = {
  standby: { dot: "bg-white/30", text: "text-white/50", label: "STANDBY" },
  listening: { dot: "bg-sky-400 animate-pulse", text: "text-sky-300", label: "LISTENING" },
  executing: { dot: "bg-amber-400 animate-pulse", text: "text-amber-400", label: "EXECUTING" },
  reporting: { dot: "bg-emerald-400 animate-pulse", text: "text-emerald-300", label: "REPORTING" },
};

export const OracleVoice = () => {
  const navigate = useNavigate();
  const [armed, setArmed] = useState(false);
  const [oracleState, setOracleState] = useState("standby");
  const [status, setStatus] = useState(null);
  const [srSupported] = useState(() =>
    Boolean(window.SpeechRecognition || window.webkitSpeechRecognition));
  const recRef = useRef(null);
  const ackUrlRef = useRef(null);
  const audioRef = useRef(null);
  const busyRef = useRef(false);
  const hotUntilRef = useRef(0); // wake-word-only heard → next utterance is a command
  const armedRef = useRef(false);

  const loadStatus = useCallback(() => {
    voiceApi.status().then(setStatus).catch(() => {});
  }, []);
  useEffect(() => { loadStatus(); const t = setInterval(loadStatus, 60000); return () => clearInterval(t); }, [loadStatus]);

  // Prefetch the instant-ack clip once — first audio must beat the sub-second gate
  useEffect(() => {
    voiceApi.tts("On it, Sovereign.").then((r) => { ackUrlRef.current = b64ToUrl(r.audio_b64); }).catch(() => {});
  }, []);

  // ---- Morning Oracle Brief — surface once per day --------------------------
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem("uc-brief-toast") === today) return;
    voiceApi.brief().then((b) => {
      if (b.played_at) return;
      localStorage.setItem("uc-brief-toast", today);
      toast.message("Morning Oracle Brief is ready, Sovereign.", {
        duration: 15000,
        icon: <Sunrise size={14} className="text-amber-400" />,
        description: `${b.episodes_count} overnight episodes · ${b.signature_items?.length || 0} awaiting signature`,
        action: {
          label: "Play Brief",
          onClick: async () => {
            try {
              const r = await voiceApi.briefAudio();
              playUrl(b64ToUrl(r.audio_b64), "reporting");
              voiceApi.briefPlayed().catch(() => {});
            } catch { toast.error("Brief audio unavailable"); }
          },
        },
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const playUrl = (url, state = "reporting") =>
    new Promise((resolve) => {
      if (audioRef.current) { audioRef.current.pause(); }
      const a = new Audio(url);
      audioRef.current = a;
      setOracleState(state);
      a.onended = () => { setOracleState(armedRef.current ? "listening" : "standby"); resolve(); };
      a.onerror = () => { setOracleState(armedRef.current ? "listening" : "standby"); resolve(); };
      a.play().catch(() => resolve());
    });

  // ---- dispatch a captured command ------------------------------------------
  const dispatch = useCallback(async (transcript, t0) => {
    if (busyRef.current) return; // one in-flight API call at a time
    busyRef.current = true;
    // barge-in: a new command interrupts any ongoing report
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setOracleState("executing");
    const m2e = { val: null };
    // Instant local acknowledgement — first audio inside the sub-second gate.
    // Mouth-to-ear is stamped when playback ACTUALLY starts (audio 'playing' event).
    if (ackUrlRef.current) {
      const ack = new Audio(ackUrlRef.current);
      ack.addEventListener("playing", () => {
        m2e.val = Math.max(1, Math.round(performance.now() - t0));
      }, { once: true });
      ack.play().catch(() => {});
    }
    try {
      const r = await voiceApi.command(transcript);
      const fullMs = Math.round(performance.now() - t0);
      voiceApi.clientLatency(r.turn_id, {
        mouth_to_ear_ms: m2e.val ?? fullMs, full_response_ms: fullMs }).catch(() => {});
      toast.message(`ORACLE · ${r.intent.replace(/_/g, " ").toUpperCase()}`, {
        description: r.speak_text.slice(0, 180), duration: 6000,
        icon: <Volume2 size={13} className="text-sky-300" /> });
      playUrl(b64ToUrl(r.audio_b64), "reporting"); // not awaited — barge-in stays possible
      if (r.navigation) navigate(r.navigation);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice command failed");
      setOracleState(armedRef.current ? "listening" : "standby");
    } finally { busyRef.current = false; }
  }, [navigate, loadStatus]);

  // ---- simulated transcript injection (testing/debug — same dispatch path) ----
  useEffect(() => {
    const h = (e) => {
      const t = e.detail?.transcript;
      if (t) dispatch(t, e.detail?.t0 ?? performance.now());
    };
    window.addEventListener("uc:voice-sim", h);
    return () => window.removeEventListener("uc:voice-sim", h);
  }, [dispatch]);

  // ---- wake word recognition -------------------------------------------------
  const startRecognition = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = "en-US";
    rec.onresult = (ev) => {
      const res = ev.results[ev.results.length - 1];
      if (!res.isFinal) return;
      const text = res[0].transcript.trim();
      const t0 = performance.now();
      const lower = text.toLowerCase();
      const wakeIdx = lower.search(/\bcommand\b/);
      if (wakeIdx >= 0) {
        const after = text.slice(wakeIdx + 7).replace(/^[,.\s]+/, "");
        if (after.length > 1) { dispatch(text, t0); }
        else { hotUntilRef.current = Date.now() + 8000; setOracleState("listening"); }
      } else if (Date.now() < hotUntilRef.current) {
        hotUntilRef.current = 0;
        dispatch(`Command, ${text}`, t0);
      }
    };
    rec.onend = () => { if (armedRef.current) { try { rec.start(); } catch { /* already running */ } } };
    rec.onerror = () => {};
    try { rec.start(); } catch { /* no-op */ }
    recRef.current = rec;
  }, [dispatch]);

  const toggleArm = async () => {
    if (armed) {
      armedRef.current = false;
      setArmed(false);
      setOracleState("standby");
      if (recRef.current) { try { recRef.current.stop(); } catch { /* no-op */ } recRef.current = null; }
      return;
    }
    armedRef.current = true;
    setArmed(true);
    setOracleState("listening");
    if (srSupported) startRecognition();
    toast.message("Voice layer armed — say: Command, …", {
      description: srSupported
        ? "Wake word active. Try: Command, status"
        : "Browser speech recognition unavailable — use push-to-talk.",
      duration: 5000 });
  };

  // ---- push-to-talk fallback (no SpeechRecognition) ---------------------------
  const pttRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const pushToTalk = async () => {
    if (recording) {
      pttRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setOracleState("executing");
        const t0 = performance.now();
        try {
          const sttT0 = performance.now();
          const { text } = await voiceApi.stt(new Blob(chunks, { type: "audio/webm" }));
          const sttMs = Math.round(performance.now() - sttT0);
          await dispatch(text.toLowerCase().includes("command") ? text : `Command, ${text}`, t0 - sttMs);
        } catch { toast.error("Transcription failed"); setOracleState("standby"); }
      };
      mr.start();
      pttRef.current = mr;
      setRecording(true);
      setOracleState("listening");
      setTimeout(() => { if (mr.state === "recording") mr.stop(); }, 8000);
    } catch { toast.error("Microphone access denied"); }
  };

  const st = STATE_STYLE[oracleState];
  return (
    <div className="flex items-center gap-2" data-testid="oracle-voice-cluster">
      <div
        className="hidden lg:flex items-center gap-2 uc-chip px-3 py-1.5 border-white/15"
        data-testid="oracle-status-bar"
        title={`Voice: ${status?.provider || "openai"} / ${status?.voice || "onyx"} · wake word "Command"`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} data-testid="oracle-state-dot" />
        <span className={`font-mono-uc text-[9px] uppercase tracking-widest ${st.text}`} data-testid="oracle-state-label">
          ORACLE · {st.label}
        </span>
        {status && (
          <span className="font-mono-uc text-[9px] uppercase text-white/40 border-l border-white/15 pl-2" data-testid="oracle-counts">
            {status.active_ventures}V · ${Number(status.revenue_today || 0).toLocaleString()} today
            {status.consensus_open > 0 && (
              <span className="text-sky-300"> · {status.consensus_open} QUEUE</span>
            )}
            {status.pending_signatures > 0 && (
              <span className="text-amber-400"> · {status.pending_signatures} SIG</span>
            )}
          </span>
        )}
      </div>
      <button
        data-testid="voice-arm-btn"
        onClick={toggleArm}
        className={`flex items-center gap-2 uc-chip px-3 py-1.5 transition-colors uc-focus ${
          armed ? "border-sky-500/60 bg-sky-500/10" : "hover:border-sky-500/40"}`}
        title={armed ? "Disarm voice layer" : 'Arm voice layer — wake word "Command"'}
      >
        {armed ? <Mic size={11} className="text-sky-300" /> : <MicOff size={11} className="text-white/50" />}
        <span className={armed ? "text-sky-300" : "text-white/50"}>
          {armed ? "Voice ON" : "Voice"}
        </span>
      </button>
      {armed && !srSupported && (
        <button
          data-testid="voice-ptt-btn"
          onClick={pushToTalk}
          className={`uc-chip px-3 py-1.5 ${recording ? "border-rose-500/60 text-rose-300 animate-pulse" : "text-sky-300"}`}
        >
          {recording ? "● REC — tap to send" : "Push to talk"}
        </button>
      )}
    </div>
  );
};
